/**
 * Progress Tracker Component
 * Real-time progress display with animations
 */

import React, { useEffect, useState } from 'react';
import { useProcessingStore } from '../store/useProcessingStore';
import { ProcessingStep, ANIMATION_MESSAGES } from '@shared/types';
import { CheckCircle, Circle, Loader2, XCircle, StopCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { joinUrl } from '../utils/url';

const stepInfo = [
  { step: ProcessingStep.UPLOADING, label: '上传文件' },
  { step: ProcessingStep.EXTRACTING_TEXT, label: '提取文本' },
  { step: ProcessingStep.INTERPRETING, label: '解读分析' },
  { step: ProcessingStep.GENERATING_SOLUTIONS, label: '生成解析' },
  { step: ProcessingStep.LOGIC_CHECKING, label: '逻辑检查' },
  { step: ProcessingStep.VALIDATING, label: '结果验证' },
  { step: ProcessingStep.GENERATING_LATEX, label: 'LaTeX生成' },
];

export const ProgressTracker: React.FC = () => {
  const { state, processId, serverUrl, setState } = useProcessingStore();
  const [animationIndex, setAnimationIndex] = useState(0);
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());
  const [stepElapsedTime, setStepElapsedTime] = useState<number>(0);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);
  const [aborting, setAborting] = useState(false);

  // Reset timer when step changes
  useEffect(() => {
    setStepStartTime(Date.now());
    setStepElapsedTime(0);
  }, [state?.currentStep]);

  // Update elapsed time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setStepElapsedTime(Math.floor((Date.now() - stepStartTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [stepStartTime]);

  useEffect(() => {
    if (!state || state.currentStep === ProcessingStep.IDLE) {
      setAnimationIndex(0);
      return;
    }

    const messages = ANIMATION_MESSAGES[state.currentStep];
    const interval = setInterval(() => {
      setAnimationIndex((prev) => (prev + 1) % messages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [state]);

  if (!state || state.currentStep === ProcessingStep.IDLE) {
    return null;
  }

  const getStepStatus = (step: ProcessingStep) => {
    if (!state) return 'pending';

    const currentIndex = stepInfo.findIndex(s => s.step === state.currentStep);
    const stepIndex = stepInfo.findIndex(s => s.step === step);

    if (state.currentStep === ProcessingStep.ERROR) {
      return stepIndex <= currentIndex ? 'error' : 'pending';
    }

    if (state.currentStep === ProcessingStep.COMPLETED) {
      return 'completed';
    }

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'processing';
    return 'pending';
  };

  const currentMessage = state ? ANIMATION_MESSAGES[state.currentStep][animationIndex] : '';

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if step is taking too long (over 5 minutes)
  const isStepTooLong = stepElapsedTime > 300;

  // Handle abort request
  const handleAbort = async () => {
    if (!processId) return;

    setAborting(true);
    try {
      await axios.post(joinUrl(serverUrl, `/api/abort/${processId}`));

      // Update local state
      setState({
        processId,
        currentStep: ProcessingStep.ERROR,
        progress: state?.progress || 0,
        statusMessages: ['用户终止了处理'],
        currentAnimation: '处理已终止',
        lastUpdated: new Date(),
        error: '用户手动终止了处理过程',
      });
    } catch (error) {
      console.error('Failed to abort:', error);
      alert('终止处理失败，请稍后重试');
    } finally {
      setAborting(false);
      setShowAbortConfirm(false);
    }
  };

  const canAbort = state &&
    state.currentStep !== ProcessingStep.IDLE &&
    state.currentStep !== ProcessingStep.COMPLETED &&
    state.currentStep !== ProcessingStep.ERROR;

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">处理进度</h2>
              <p className="mt-1 text-blue-100">
                {state.currentStep === ProcessingStep.COMPLETED
                  ? '处理完成！'
                  : state.currentStep === ProcessingStep.ERROR
                  ? '处理出错'
                  : '正在处理...'}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {state.currentStep !== ProcessingStep.COMPLETED &&
               state.currentStep !== ProcessingStep.ERROR && (
                <div className="text-right">
                  <div className={`text-3xl font-mono font-bold ${isStepTooLong ? 'text-yellow-300 animate-pulse' : 'text-white'}`}>
                    {formatTime(stepElapsedTime)}
                  </div>
                  <div className="text-xs text-blue-100 mt-1">
                    {isStepTooLong ? '⚠️ 步骤超时' : '当前步骤用时'}
                  </div>
                </div>
              )}
              {canAbort && !showAbortConfirm && (
                <button
                  onClick={() => setShowAbortConfirm(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all shadow-lg"
                  disabled={aborting}
                >
                  <StopCircle className="w-5 h-5" />
                  <span>终止</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Abort Confirmation */}
        {showAbortConfirm && canAbort && (
          <div className="bg-yellow-50 border-t-4 border-yellow-500 p-6">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-yellow-900">确认终止处理？</h3>
                <p className="mt-2 text-yellow-800">
                  终止后当前处理将停止，所有进度将丢失。此操作无法撤销。
                </p>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={handleAbort}
                    disabled={aborting}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
                  >
                    {aborting ? '正在终止...' : '确认终止'}
                  </button>
                  <button
                    onClick={() => setShowAbortConfirm(false)}
                    disabled={aborting}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-all"
                  >
                    取消
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="p-6">
          <div className="relative">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-500"
                style={{ width: `${state.progress}%` }}
              ></div>
            </div>
            <div className="mt-2 text-right text-sm font-medium text-gray-600">
              {state.progress}%
            </div>
          </div>

          {/* Animation Message */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-900 font-medium animate-pulse">
                {currentMessage}
              </p>
            </div>
          </div>

          {/* Step List */}
          <div className="mt-8 space-y-4">
            {stepInfo.map((info, index) => {
              const status = getStepStatus(info.step);

              return (
                <div
                  key={info.step}
                  className={`
                    flex items-center space-x-4 p-4 rounded-lg transition-all
                    ${status === 'processing' ? 'bg-blue-50 border-2 border-blue-300' :
                      status === 'completed' ? 'bg-green-50 border border-green-200' :
                      status === 'error' ? 'bg-red-50 border border-red-200' :
                      'bg-gray-50 border border-gray-200'}
                  `}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {status === 'completed' ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : status === 'processing' ? (
                      <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    ) : status === 'error' ? (
                      <XCircle className="w-6 h-6 text-red-600" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-400" />
                    )}
                  </div>

                  {/* Label */}
                  <div className="flex-1">
                    <p className={`
                      font-medium
                      ${status === 'completed' ? 'text-green-700' :
                        status === 'processing' ? 'text-blue-700' :
                        status === 'error' ? 'text-red-700' :
                        'text-gray-600'}
                    `}>
                      {info.label}
                    </p>
                  </div>

                  {/* Step Number */}
                  <div className={`
                    text-sm font-medium px-3 py-1 rounded-full
                    ${status === 'completed' ? 'bg-green-100 text-green-700' :
                      status === 'processing' ? 'bg-blue-100 text-blue-700' :
                      status === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'}
                  `}>
                    {index + 1}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error Message */}
          {state.error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 font-medium">错误：{state.error}</p>
            </div>
          )}

          {/* Status Messages */}
          {state.statusMessages.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">详细日志</h3>
              <div className="bg-gray-900 rounded-lg p-4 max-h-48 overflow-y-auto font-mono text-xs text-green-400">
                {state.statusMessages.map((msg, i) => (
                  <div key={i} className="mb-1">
                    [{new Date().toLocaleTimeString()}] {msg}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
